import { CollectionSVG, type SvgComponentType } from "@newsela/angelou";

import { useNavigationBarV2Context } from "@/base/NewselaGlobalLayoutV2/NavigationBar/NavigationBar.provider";
import {
  leftContainerCss,
  libraryMenuPopoutContainer,
  rightContainerCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/ResourceMenus/ContentLibraryMenu/ContentLibraryPopout/ContentLibraryPopout.styles";
import {
  EXPLORE_CONTENT_ITEMS,
  MY_CONTENT_ITEMS,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/constants";
import { PopoutMenuSection } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/PopoutMenuSection";
import { PopoutSectionItem } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/PopoutSectionItem";
import {
  PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE,
  USER_FEATURE_FOR_PROMOTED_AT_YOUR_DISTRICT_PAGE,
  USER_FEATURE_FOR_PROMOTED_AT_YOUR_SCHOOL_PAGE,
} from "@/components/ContentPromotions/constants";
import useHasFeature from "@/features/useHasFeature";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";

import useUserFeaturesQuery from "@/users/v3/useUserFeaturesQuery";

const ContentLibraryPopout = () => {
  const {
    getters: { customCollections, personalizedCollections },
  } = useNavigationBarV2Context();

  const { data: features } = useUserFeaturesQuery();

  const { data: user } = useUser();
  const { isStudent, isLiteUser } = getUserProperties(user, {
    shouldUseNewUserApi: false,
  });
  const [hasUserFeatureToAccessPromotedAtYourDistrictPage] = useHasFeature(
    USER_FEATURE_FOR_PROMOTED_AT_YOUR_DISTRICT_PAGE,
  );
  const [hasUserFeatureToAccessPromotedAtYourSchoolPage] = useHasFeature(
    USER_FEATURE_FOR_PROMOTED_AT_YOUR_SCHOOL_PAGE,
  );

  const activeStemLicense = features?.includes("has_stem_license");
  const activeELALicense = features?.includes("has_ela_license");
  const canSeeGenerationGenius =
    activeStemLicense || activeELALicense || isLiteUser;

  return (
    <div css={libraryMenuPopoutContainer}>
      <div css={leftContainerCss}>
        <PopoutMenuSection title="My content" width="14.375rem">
          {MY_CONTENT_ITEMS.filter(
            (item) =>
              !(
                item.href === PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.district &&
                !hasUserFeatureToAccessPromotedAtYourDistrictPage
              ),
          )
            .filter(
              (item) =>
                !(
                  item.href === PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.school &&
                  !hasUserFeatureToAccessPromotedAtYourSchoolPage
                ),
            )
            .filter((item) => !isStudent || item.showForStudents)
            .map(({ title, href, SvgComponent, dataQASelector, badge }) => (
              <PopoutSectionItem
                key={title}
                href={href}
                badge={badge}
                title={title}
                SvgComponent={SvgComponent}
                dataQASelector={dataQASelector}
              />
            ))}
          {personalizedCollections.map(({ title, href }) => (
            <PopoutSectionItem
              key={title}
              href={href}
              title={title}
              SvgComponent={CollectionSVG as SvgComponentType}
              dataQASelector={
                title
                  ? `global-nav-${title.toLowerCase().replaceAll(" ", "-")}`
                  : ""
              }
            />
          ))}
          {customCollections.map(({ title, href }) => (
            <PopoutSectionItem
              key={title}
              href={href}
              title={title}
              SvgComponent={CollectionSVG as SvgComponentType}
              dataQASelector={
                title
                  ? `global-nav-${title.toLowerCase().replaceAll(" ", "-")}`
                  : ""
              }
            />
          ))}
        </PopoutMenuSection>
      </div>

      <div css={rightContainerCss}>
        <PopoutMenuSection title="Explore content" width="14.375rem">
          {EXPLORE_CONTENT_ITEMS.filter((item) => {
            if (item.isGenerationGenius) {
              return canSeeGenerationGenius;
            }
            return (
              (!isStudent || item.showForStudents) &&
              (!isLiteUser || item.showForLite)
            );
          }).map(
            (
              {
                title,
                href,
                SvgComponent,
                dataQASelector,
                badge,
                openInNewTab,
                isGenerationGenius,
              },
              index,
            ) => (
              <PopoutSectionItem
                key={index}
                href={href}
                title={title}
                badge={badge}
                openInNewTab={openInNewTab}
                SvgComponent={
                  isGenerationGenius
                    ? (props: any) => <SvgComponent {...props} size={24} />
                    : SvgComponent
                }
                dataQASelector={dataQASelector}
              />
            ),
          )}
        </PopoutMenuSection>
      </div>
    </div>
  );
};

ContentLibraryPopout.displayName = "ContentLibraryPopout";
export { ContentLibraryPopout };
